import { useRef, useEffect, useState, memo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useStore } from '../../../store/useStore';
import type { Enemy } from '../../../schema/GameState';
import * as Colyseus from 'colyseus.js';
import { RigidBody, RapierRigidBody, CapsuleCollider } from '@react-three/rapier';
import { HealthBar } from '../ui/HealthBar';
import { CharacterPlaceholder } from '../characters/CharacterPlaceholder';
import { DeathExplosionEffect } from '../vfx/Effects';
import { getHeight } from '../Terrain';

const _target = new THREE.Vector3();
const _vecB = new THREE.Vector3();

export const EnemyRenderer = ({ room, scene = "arena" }: { room: Colyseus.Room<any> | null, scene?: string }) => {
    if (!room) return null;
    return <MapToComponents room={room} scene={scene} />;
};

const MapToComponents = ({ room, scene }: { room: Colyseus.Room<any>, scene: string }) => {
    const [enemyList, setEnemyList] = useState<{ id: string, data: Enemy }[]>([]);
    const [deathEffects, setDeathEffects] = useState<Array<{ id: string; pos: THREE.Vector3; time: number }>>([]);
    const theme = useStore(state => state.theme);
    const updateQuestProgress = useStore(state => state.updateQuestProgress);
    const addStat = useStore(state => state.addStat);
    const prevHpRef = useRef<Map<string, number>>(new Map());

    useEffect(() => {
        if (!room || !room.state || !room.state.enemies) return;

        const updateEnemies = () => {
            const list: { id: string, data: Enemy }[] = [];
            room.state.enemies.forEach((e: Enemy, id: string) => {
                const eScene = e.currentScene || "arena";
                if (eScene === scene) {
                    list.push({ id, data: e });

                    // Check for death and spawn explosion
                    const prevHp = prevHpRef.current.get(id) || 100;
                    if (e.hp <= 0 && prevHp > 0) {
                        // Enemy just died
                        const explosionId = `death-${id}-${Date.now()}`;
                        setDeathEffects(prev => [...prev, { 
                            id: explosionId, 
                            pos: new THREE.Vector3(e.x, e.y + 1, e.z), 
                            time: Date.now() 
                        }]);
                        setTimeout(() => {
                            setDeathEffects(prev => prev.filter(x => x.id !== explosionId));
                        }, 600);
                        updateQuestProgress('kill', 'enemy', 1);
                        addStat('kill', 1);
                    }
                    prevHpRef.current.set(id, e.hp);
                }
            });
            setEnemyList([...list]);
        };

        room.state.enemies.onAdd(() => updateEnemies());
        room.state.enemies.onRemove(() => updateEnemies());
        updateEnemies();

    }, [room]);

    return (
        <group>
            {enemyList.map((item) => (
                <EnemyActor key={item.id} enemy={item.data} theme={theme} scene={scene} />
            ))}
            
            {/* Death Explosion VFX */}
            {deathEffects.map(effect => (
                <DeathExplosionEffect
                    key={effect.id}
                    position={effect.pos}
                    scale={1}
                />
            ))}
        </group>
    );
};

const EnemyActor = memo(({ enemy, theme, scene }: { enemy: Enemy, theme: 'medieval' | 'cyberpunk', scene: string }) => {
    const rb = useRef<RapierRigidBody>(null);
    const capsuleRef = useRef<any>(null);
    const [anim, setAnim] = useState('idle');
    const prevPos = useRef(new THREE.Vector3(enemy.x, enemy.y, enemy.z));
    const visualRef = useRef<THREE.Group>(null);

    // Set userData on collider after mount
    useEffect(() => {
        if (capsuleRef.current) {
            capsuleRef.current.userData = { type: 'enemy', id: enemy.id };
        }
    }, [enemy.id]);

    useFrame(() => {
        if (!rb.current) return;

        // 1. Move towards target (Reusing global _target v3)
        const groundHeight = scene === 'world' ? getHeight(enemy.x, enemy.z) : 0;
        _target.set(enemy.x, groundHeight, enemy.z);

        // Kinematic Movement (Consolidated vector math)
        const currentPos = rb.current.translation();
        _vecB.set(currentPos.x, currentPos.y, currentPos.z).lerp(_target, 0.1);
        rb.current.setNextKinematicTranslation(_vecB);

        // 2. Simple movement detection for animation
        const distSq = prevPos.current.distanceToSquared(_target);

        // Priority: Attack > Run > Idle
        if (enemy.state === 'attack') {
            if (anim !== 'attack') setAnim('attack');
        } else if (distSq > 0.0001 || enemy.state === 'chase' || enemy.state === 'return') {
            if (anim !== 'run') setAnim('run');

            // Visual Rotation (Moved into same frame loop)
            if (visualRef.current) {
                const angle = Math.atan2(_target.x - prevPos.current.x, _target.z - prevPos.current.z);
                let currentRot = visualRef.current.rotation.y;
                let delta = angle - currentRot;
                while (delta < -Math.PI) delta += Math.PI * 2;
                while (delta > Math.PI) delta -= Math.PI * 2;
                visualRef.current.rotation.y += delta * 0.1;
            }
        } else {
            if (anim !== 'idle') setAnim('idle');
        }
        prevPos.current.copy(_target);
    });

    // Simplified actor structure
    return (
        <RigidBody
            ref={rb}
            type="kinematicPosition"
            position={[enemy.x, enemy.y, enemy.z]}
            colliders={false} // Custom collider
        >
            <CapsuleCollider ref={capsuleRef} args={[0.7, 0.4]} position={[0, 0.7, 0]} />
            <group ref={visualRef}>
                {/* Visual Model */}
                <CharacterPlaceholder theme={theme} animation={anim} isEnemy={true} />
                {/* UI Overlay */}
                <HealthBar hp={enemy.hp} maxHp={100} offset={[0, 1.8, 0]} />
            </group>
        </RigidBody>
    );
});
